"use client";

import {ContextLoadingOverlay} from "@/components/shared/contextLoader";
import {metaService} from "@/service/meta.service";
import {createContext, useContext, useEffect, useState} from "react";
const NeoContext = createContext<NeoContextProps>({} as NeoContextProps);

/**
 * Suggestion
 *
 *  We already have sufficient props for now, only add a new prop if its really required
 *  please extend type context
 *
 */

export const NeoContextProvider = ({children}: NeoContextProviderProps) => {
  const [operators, setOperators] = useState<OperatorLookup[]>([]);
  const [categories, setCategories] = useState<CategoryLookup[]>([]);
  const [vehicles, setVehicles] = useState<VehicleInfo[]>([]);
  const [facility, setFacility] = useState<Facility[]>([]);
  const [roleAccess, setRoleAccess] = useState<RoleAccess[]>([]);
  const [currency, setCurrency] = useState("");
  const [offset, setoffset] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [hwFeatures, setHWFeatures] = useState<any>([]);
  const [uhfHardwareGroups, setUhfHardwareGroups] = useState<any>([]);
  const [routePlanCoins, setRoutePlanCoins] = useState<number>(0);
  const [selectedFilter, setSelectedFilter] = useState<IFilterForm>({
    institution: undefined
  });
  const [loadingContext, setLoadingContext] = useState<boolean>(true);
  const [filterContext, setFilterContext] = useState<FilterContext>({
    client: {
      institutionId: null,
      institutionUserId: null,
      institutionUserName: null,
      institutionName: null,
      institutionUserPhone: null,
      name: null,
      roles: []
    }
  });

  const fetchData = async () => {
    setLoadingContext(true);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetInMinutes = new Date().getTimezoneOffset();
    const positiveOffsetInMinutes = Math.abs(offsetInMinutes);
    const offsetInSeconds: any = positiveOffsetInMinutes * 60;
    setoffset(offsetInSeconds);
    await metaService
      .onGetClientProfile()
      .then((response: NeoClients) => {
        setOperators(response.lookup.operators!);
        setCategories(response.lookup.categories!);
        setVehicles(response.lookup.vehicles!);
        setFacility(response.lookup.facility!);
        setHWFeatures(response.lookup.hwFeatures!);
        setUhfHardwareGroups(response.lookup.uhfHardwareGroups);
        setClients(response.clients);
        setFilterContext({
          client: response?.default
        });
        setSelectedFilter({
          institution: response.default.institutionUserId!
        });
        if (response.roles) {
          setRoleAccess(response.roles ?? []);
        }
        setCurrency(response.default.currency || "INR");
      })
      .finally(() => {
        setLoadingContext(false);
      });
  };

  const getRoutePlanCoins = async () => {
    await metaService
      .getInstitutionCreditCoins()
      .then((response: {credits: number} | null) => {
        if (response?.credits) {
          setRoutePlanCoins(response.credits || 0);
        } else {
          setRoutePlanCoins(0);
        }
      });
  };
  // No need in SME type users will enable later
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (filterContext.client.institutionId) {
      getRoutePlanCoins();
      intervalId = setInterval(() => {
        getRoutePlanCoins();
      }, 10000); // 10 second
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [filterContext.client.institutionId]);

  useEffect(() => {
    fetchData();
  }, [filterContext.client.institutionId]);

  return (
    <NeoContext.Provider
      value={{
        categories,
        operators,
        clients,
        hwFeatures,
        uhfHardwareGroups,
        vehicles,
        filterContext,
        setFilterContext,
        selectedFilter,
        setSelectedFilter,
        roleAccess,
        offset,
        currency,
        routePlanCoins
      }}
    >
      {loadingContext ? <ContextLoadingOverlay /> : children}
    </NeoContext.Provider>
  );
};
export const useNeoContext = () => useContext<NeoContextProps>(NeoContext);
